import React, { createContext, useState, useEffect, useContext } from 'react';
import { DEFAULT_COLOR_SCHEME, resolveColorScheme } from '../theme/maveshiTheme';
import { syncMaveshiCssVars } from '../theme/syncMaveshiCssVars';

const MOBILE_COMFORT_QUERY = '(max-width: 768px)';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved || 'light';
  });

  // Default density is the normal (non-compact) layout. The old 'themeCompact'
  // key always held the previous compact default, so it is dropped once.
  const [isCompact, setIsCompact] = useState(() => {
    localStorage.removeItem('themeCompact');
    const saved = localStorage.getItem('themeCompactMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [isMobileComfort, setIsMobileComfort] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_COMFORT_QUERY).matches;
  });

  const [colorScheme, setColorSchemeState] = useState(() => {
    const saved = localStorage.getItem('themeColorScheme');
    return resolveColorScheme(saved);
  });

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
    document.body.className = document.body.className.replace(/theme-light|theme-dark/g, '');
    document.body.classList.add(`theme-${themeMode}`);
    document.documentElement.setAttribute('data-theme', themeMode);
  }, [themeMode]);

  useEffect(() => {
    localStorage.setItem('themeCompactMode', JSON.stringify(isCompact));
    document.body.classList.toggle('theme-compact', isCompact);
    document.documentElement.classList.toggle('theme-compact', isCompact);
    document.documentElement.setAttribute('data-compact', isCompact ? 'true' : 'false');
  }, [isCompact]);

  useEffect(() => {
    localStorage.setItem('themeColorScheme', colorScheme);
    document.documentElement.setAttribute('data-color-scheme', colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_COMFORT_QUERY);
    const updateMobileComfort = (event) => {
      setIsMobileComfort(event.matches);
    };

    setIsMobileComfort(mediaQuery.matches);
    mediaQuery.addEventListener('change', updateMobileComfort);
    return () => mediaQuery.removeEventListener('change', updateMobileComfort);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('viewport-mobile', isMobileComfort);
    document.documentElement.setAttribute('data-mobile-comfort', isMobileComfort ? 'true' : 'false');
  }, [isMobileComfort]);

  useEffect(() => {
    syncMaveshiCssVars(themeMode, isCompact, colorScheme, isMobileComfort);
  }, [themeMode, isCompact, colorScheme, isMobileComfort]);

  const toggleCompact = () => {
    setIsCompact(prev => !prev);
  };

  const setTheme = (mode) => {
    setThemeMode(mode);
  };

  const setColorScheme = (scheme) => {
    setColorSchemeState(resolveColorScheme(scheme));
  };

  const resetColorScheme = () => {
    setColorSchemeState(DEFAULT_COLOR_SCHEME);
  };

  const value = {
    themeMode,
    isCompact,
    isMobileComfort,
    colorScheme,
    toggleCompact,
    setIsCompact,
    setTheme,
    setColorScheme,
    resetColorScheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
